import { useEffect } from "react";
import { downloadUrl, fileUrl, thumbUrl } from "../api";
import { formatJpy, formatJpyWithUsd } from "../money";
import type { ImageItem } from "../types";
import { IconArrow, IconClose, IconDownload, IconImage, IconStar } from "./icons";
import { cx } from "./ui";

export function Lightbox({
  images,
  index,
  modelLabels,
  onIndex,
  onClose,
  onToggleFavorite,
  onQuote,
}: {
  images: ImageItem[];
  index: number;
  modelLabels: Record<string, string>;
  onIndex: (i: number) => void;
  onClose: () => void;
  onToggleFavorite: (image: ImageItem) => void;
  onQuote: (image: ImageItem) => void;
}) {
  const image = images[index];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex((index + 1) % images.length);
      if (e.key === "ArrowLeft") onIndex((index - 1 + images.length) % images.length);
      if (e.key.toLowerCase() === "f" && image) onToggleFavorite(image);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, images.length, image, onClose, onIndex, onToggleFavorite]);

  if (!image?.file_name) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-900/92 backdrop-blur-sm" onClick={onClose}>
      <header
        className="flex items-center gap-3 px-4 py-3 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-[15px] font-bold">#{image.idx + 1}</span>
        {image.variant_label && (
          <span className="rounded bg-brand-500 px-2 py-0.5 text-[12px] font-bold text-white">
            {image.variant_label}
          </span>
        )}
        {image.tags.length > 0 && (
          <span className="rounded bg-white/15 px-2 py-0.5 text-[12px] font-semibold text-white/90">
            🏷 {image.tags.join("・")}
          </span>
        )}
        <span className="text-[13px] text-white/70">
          {modelLabels[image.model] ?? image.model} · {image.aspect_ratio} ·{" "}
          {image.width && image.height ? `${image.width}×${image.height}` : image.size} · 品質{image.quality} ·{" "}
          {(image.duration_ms / 1000).toFixed(1)}s ·{" "}
          <span title={formatJpyWithUsd(image.cost_usd)}>{formatJpy(image.cost_usd)}</span>
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => onQuote(image)}
            title="この画像を参照画像として引用"
            className="flex h-9 items-center gap-1.5 rounded-lg bg-white/10 px-3 text-[13px] font-semibold text-white/90 transition hover:bg-white/20"
          >
            <IconImage className="h-4 w-4" />
            引用して次を作る
          </button>
          <button
            type="button"
            onClick={() => onToggleFavorite(image)}
            title="お気に入り (F)"
            className={cx(
              "grid h-9 w-9 place-items-center rounded-lg transition",
              image.favorite ? "bg-amber-400 text-white" : "bg-white/10 text-white/80 hover:bg-white/20",
            )}
          >
            <IconStar filled={image.favorite} />
          </button>
          <a
            href={downloadUrl(image.id)}
            title="ダウンロード"
            className="grid h-9 w-9 place-items-center rounded-lg bg-white/10 text-white/80 transition hover:bg-white/20"
          >
            <IconDownload />
          </a>
          <button
            type="button"
            onClick={onClose}
            title="閉じる (Esc)"
            className="grid h-9 w-9 place-items-center rounded-lg bg-white/10 text-white/80 transition hover:bg-white/20"
          >
            <IconClose />
          </button>
        </div>
      </header>

      <div className="relative flex flex-1 items-center justify-center px-4 pb-4">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onIndex((index - 1 + images.length) % images.length);
          }}
          className="absolute left-3 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
          aria-label="前へ"
        >
          <IconArrow dir="left" className="h-5 w-5" />
        </button>
        <img
          src={fileUrl(image.file_name)}
          alt={`案 ${image.idx + 1}`}
          onClick={(e) => e.stopPropagation()}
          className="max-h-full max-w-full rounded-xl object-contain shadow-2xl"
        />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onIndex((index + 1) % images.length);
          }}
          className="absolute right-3 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
          aria-label="次へ"
        >
          <IconArrow className="h-5 w-5" />
        </button>
      </div>

      <footer
        className="flex justify-center gap-1.5 overflow-x-auto px-4 pb-4 thin-scroll"
        onClick={(e) => e.stopPropagation()}
      >
        {images.map((img, i) => (
          <button
            key={img.id}
            type="button"
            onClick={() => onIndex(i)}
            className={cx(
              "h-12 w-12 shrink-0 overflow-hidden rounded-lg border-2 transition",
              i === index ? "border-brand-400" : "border-transparent opacity-50 hover:opacity-90",
            )}
          >
            {img.file_name && <img src={thumbUrl(img.file_name, 96)} alt="" className="h-full w-full object-cover" />}
          </button>
        ))}
      </footer>
    </div>
  );
}
